import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

export interface SendForBiddingDialogData {
  boq_id: number;
  project_id: number;
  boq_code: string;
  boq_name: string;
  project_name: string;
}

@Component({
  selector: 'app-send-for-bidding-dialog',
  templateUrl: './send-for-bidding-dialog.component.html',
  styleUrls: ['./send-for-bidding-dialog.component.css']
})
export class SendForBiddingDialogComponent implements OnInit {
  form: FormGroup;
  consultancyOrgs: any[] = [];
  loading = false;
  submitting = false;
  minBidClosingDate = '';
  fees: { bid_fee?: number; rate_pct?: number; boq_total?: number; currency?: string; collection?: string } | null = null;
  feesLoading = false;

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '96px',
    minHeight: '72px',
    placeholder: 'Add any specific requirements or instructions for the bidders...',
    translate: 'no',
    defaultParagraphSeparator: 'p',
    toolbarHiddenButtons: [
      ['insertVideo', 'insertHorizontalRule', 'removeFormat', 'insertImage'],
      ['subscript', 'superscript', 'fontName', 'fontSize', 'textColor', 'backgroundColor', 'customClasses']
    ]
  };

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private toastr: ToastrMsgService,
    public dialogRef: MatDialogRef<SendForBiddingDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SendForBiddingDialogData
  ) {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    this.minBidClosingDate = now.toISOString().slice(0, 16);
    const defaultClose = new Date();
    defaultClose.setDate(defaultClose.getDate() + 30);
    defaultClose.setMinutes(defaultClose.getMinutes() - defaultClose.getTimezoneOffset());
    this.form = this.fb.group({
      invited_org_ids: [[]],
      invited_emails: [[]],
      bid_closing_at: [defaultClose.toISOString().slice(0, 16)],
      requirements: ['']
    });
  }

  hasAnyInvite(): boolean {
    return this.inviteCount > 0;
  }

  get inviteCount(): number {
    const orgIds = this.form.get('invited_org_ids')?.value || [];
    const emails = this.form.get('invited_emails')?.value || [];
    const validEmails = emails.filter((e: any) => typeof e === 'string' && e.includes('@'));
    const orgCount = Array.isArray(orgIds) ? orgIds.length : 0;
    return orgCount + validEmails.length;
  }

  formatMoney(value: number | null | undefined): string {
    const n = Number(value) || 0;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: this.fees?.currency || 'INR',
      maximumFractionDigits: 0
    }).format(n);
  }

  ngOnInit(): void {
    this.loadConsultancyOrganizations();
    this.loadFees();
  }

  loadFees(): void {
    if (!this.data?.boq_id) return;
    this.feesLoading = true;
    this.apiService.getBoQFees(this.data.boq_id).subscribe({
      next: (res: any) => {
        this.fees = res?.data || null;
        this.feesLoading = false;
      },
      error: () => {
        this.fees = null;
        this.feesLoading = false;
      }
    });
  }

  loadConsultancyOrganizations(): void {
    this.loading = true;
    this.apiService.getConsultancyOrganizations().subscribe({
      next: (res: any) => {
        this.consultancyOrgs = res.data || [];
        this.loading = false;
      },
      error: (err) => {
        this.toastr.error(err.error?.error || 'Failed to load consultancy organizations');
        this.loading = false;
      }
    });
  }

  onSubmit(): void {
    this.form.markAllAsTouched();
    if (!this.hasAnyInvite()) {
      this.toastr.error('Please select at least one organization or add at least one email');
      return;
    }

    const invitedOrgIds = this.form.get('invited_org_ids')?.value || [];
    const emailsRaw = this.form.get('invited_emails')?.value || [];
    const invitedEmails = emailsRaw
      .filter((e: any) => typeof e === 'string' && e.includes('@'))
      .map((e: string) => e.trim().toLowerCase());

    this.submitting = true;
    const bidClosingAtRaw = this.form.get('bid_closing_at')?.value;
    let bidClosingAt: string | undefined;
    if (bidClosingAtRaw) {
      const d = new Date(bidClosingAtRaw);
      if (isNaN(d.getTime())) {
        this.submitting = false;
        this.toastr.error('Please enter a valid bid closing date and time');
        return;
      }
      bidClosingAt = d.toISOString();
    }
    this.apiService.createBiddingInvitation({
      boq_id: this.data.boq_id,
      project_id: this.data.project_id,
      invited_org_ids: invitedOrgIds,
      invited_emails: invitedEmails.length ? invitedEmails : undefined,
      bid_closing_at: bidClosingAt,
      requirements: this.form.get('requirements')?.value || undefined
    }).subscribe({
      next: (res: any) => {
        this.submitting = false;
        this.toastr.success('Bidding invitation sent successfully');
        this.dialogRef.close({ success: true, data: res.data });
      },
      error: (err) => {
        this.submitting = false;
        this.toastr.error(err.error?.error || 'Failed to send invitation');
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
